import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Search } from "lucide-react";
import { formatCurrency } from "@/utils/format";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

interface Store {
  id: string;
  store_name: string;
  commission_rate: number;
  total_orders: number;
  total_revenue: number;
  total_commission: number;
}

const CommissionManagement = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingStore, setEditingStore] = useState<string | null>(null);
  const [newRate, setNewRate] = useState<number | null>(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (!user || !isAdmin) {
      navigate("/");
      return;
    }
    fetchStores();
  }, [user, isAdmin, authLoading, navigate]);

  const fetchStores = async () => {
    try {
      // First, get all stores with their commission rates
      const { data: storesData, error: storesError } = await supabase
        .from("store_profiles")
        .select("id, store_name, commission_rate");

      if (storesError) throw storesError;

      // Then, get order statistics for each store
      const storesWithStats = await Promise.all(
        storesData.map(async (store) => {
          const { data: ordersData } = await supabase
            .from("orders")
            .select("total_amount, commission_amount")
            .eq("florist_id", store.id);

          const totalOrders = ordersData?.length || 0;
          const totalRevenue = ordersData?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0;
          const totalCommission = ordersData?.reduce((sum, order) => sum + (order.commission_amount || 0), 0) || 0;

          return {
            ...store,
            total_orders: totalOrders,
            total_revenue: totalRevenue,
            total_commission: totalCommission,
          };
        })
      );

      setStores(storesWithStats);
    } catch (error) {
      console.error("Error fetching stores:", error);
      toast.error("Failed to fetch stores");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCommissionRate = async (storeId: string) => {
    if (newRate === null || newRate < 0 || newRate > 100) {
      toast.error("Please enter a valid commission rate (0-100)");
      return;
    }

    setUpdating(true);
    try {
      const { error } = await supabase
        .from("store_profiles")
        .update({ commission_rate: newRate })
        .eq("id", storeId);

      if (error) throw error;

      toast.success("Commission rate updated successfully");
      setEditingStore(null);
      setNewRate(null);
      fetchStores();
    } catch (error) {
      console.error("Error updating commission rate:", error);
      toast.error("Failed to update commission rate");
    } finally {
      setUpdating(false);
    }
  };

  const filteredStores = stores.filter((store) =>
    store.store_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout currentPage="Commission">
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold">Commission Management</h1>
          <p className="text-muted-foreground">
            Manage commission rates and view earnings for each store
          </p>
        </div>

        {/* Search and Stats */}
        <div className="flex items-center justify-between">
          <div className="relative w-72">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search stores..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="text-sm text-muted-foreground">
            Total Stores: {stores.length}
          </div>
        </div>

        {/* Stores Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Store Name</TableHead>
                <TableHead>Commission Rate</TableHead>
                <TableHead>Total Orders</TableHead>
                <TableHead>Total Revenue</TableHead>
                <TableHead>Total Commission</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : filteredStores.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    No stores found
                  </TableCell>
                </TableRow>
              ) : (
                filteredStores.map((store) => (
                  <TableRow key={store.id}>
                    <TableCell>{store.store_name}</TableCell>
                    <TableCell>
                      {editingStore === store.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={newRate ?? ""}
                            onChange={(e) => setNewRate(parseFloat(e.target.value))}
                            className="w-24"
                          />
                          <Button
                            size="sm"
                            onClick={() => handleUpdateCommissionRate(store.id)}
                            disabled={updating}
                          >
                            {updating ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Save"
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingStore(null);
                              setNewRate(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        `${store.commission_rate?.toFixed(1) || 0}%`
                      )}
                    </TableCell>
                    <TableCell>{store.total_orders}</TableCell>
                    <TableCell>{formatCurrency(store.total_revenue)}</TableCell>
                    <TableCell>{formatCurrency(store.total_commission)}</TableCell>
                    <TableCell>
                      {editingStore !== store.id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingStore(store.id);
                            setNewRate(store.commission_rate);
                          }}
                        >
                          Edit Rate
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CommissionManagement; 